// Slice 54 — License detail (full-page). Tabs: Overview, Telemetry,
// Audit, Danger Zone. Lazy-fetched per tab so the screen doesn't pay for
// telemetry / audit roundtrips up-front. The license bytes come from the
// existing GET /admin/licenses/:id detail-route; mutations (extend / change
// tier / revoke / resend-email) are wired against the Slice 54 drill routes.

import { useCallback, useEffect, useState } from "react";
import {
  Api,
  type CustomerAuditRow,
  CustomersApi,
  type LicenseDetailRow,
  type LicenseTelemetryRollup,
  type Tier,
} from "../../api/client";
import { useRouter } from "../../router/router";
import { useShell } from "../../shell/HqShellContext";
import { HqBadge, HqButton, HqCard, HqInput, HqModal, Icons } from "../../ui";
import { MonoId } from "./MonoId";
import { expiryTone, fmtDateSec } from "./format";

type TabId = "overview" | "telemetry" | "audit" | "danger";

const TABS: ReadonlyArray<{ id: TabId; label: string; icon: keyof typeof Icons }> = [
  { id: "overview", label: "Overview", icon: "Key" },
  { id: "telemetry", label: "Telemetry", icon: "Telemetry" },
  { id: "audit", label: "Audit", icon: "Audit" },
  { id: "danger", label: "Danger Zone", icon: "Revoke" },
];

export interface LicenseDetailScreenProps {
  licenseId: string;
}

export function LicenseDetailScreen({ licenseId }: LicenseDetailScreenProps) {
  const { navigate } = useRouter();
  const { toast, refreshTick, bumpRefresh } = useShell();
  const [detail, setDetail] = useState<LicenseDetailRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<TabId>("overview");

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr(null);
    const res = await Api.getLicense(licenseId);
    setLoading(false);
    if (!res.ok) {
      setErr(res.error.message);
      setDetail(null);
      return;
    }
    setDetail(res.data);
  }, [licenseId]);

  useEffect(() => {
    void refreshTick;
    void refresh();
  }, [refresh, refreshTick]);

  if (loading && !detail) return <Centered tone="dim">Loading license…</Centered>;
  if (err) return <Centered tone="danger">{err}</Centered>;
  if (!detail) return <Centered tone="danger">License not found.</Centered>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Crumbs onBack={() => navigate("licenses")} />
      <Header detail={detail} />
      <KpiStrip detail={detail} />
      <TabBar tab={tab} onChange={setTab} />
      <TabBody
        tab={tab}
        detail={detail}
        onRefresh={() => {
          void refresh();
          bumpRefresh();
        }}
        toast={toast}
        navigate={navigate}
      />
    </div>
  );
}

function Centered({ tone, children }: { tone: "dim" | "danger"; children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: 48,
        textAlign: "center",
        fontSize: 13,
        color: tone === "danger" ? "var(--hq-danger)" : "var(--hq-text-dim)",
      }}
    >
      {children}
    </div>
  );
}

function Crumbs({ onBack }: { onBack: () => void }) {
  return (
    <div style={{ display: "flex" }}>
      <HqButton variant="ghost" size="sm" leadingIcon={Icons.ChevD} onClick={onBack}>
        Back to Licenses
      </HqButton>
    </div>
  );
}

function Header({ detail }: { detail: LicenseDetailRow }) {
  const tone = detail.tier === "pro" ? "pro" : detail.tier === "beta" ? "beta" : "free";
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        paddingBottom: 14,
        borderBottom: "1px solid var(--hq-border)",
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--hq-bg-input)",
          border: "1px solid var(--hq-border-input)",
          borderRadius: "var(--hq-radius-md)",
        }}
      >
        <Icons.Key width={28} height={28} sw={1.6} style={{ color: "var(--hq-accent)" }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 22, fontWeight: 600, letterSpacing: "-.01em" }}>
            <MonoId value={detail.licenseId} short={24} color="var(--hq-text)" />
          </span>
          <HqBadge tone={tone}>{detail.tier}</HqBadge>
          {detail.revoked ? (
            <HqBadge tone="danger">revoked</HqBadge>
          ) : (
            <HqBadge tone="success">active</HqBadge>
          )}
          {detail.betaProgram && <HqBadge tone="accent">beta program</HqBadge>}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginTop: 6,
            color: "var(--hq-text-muted)",
            fontSize: 12,
          }}
        >
          customer&nbsp;
          <MonoId value={detail.customerId} short={18} color="var(--hq-text-muted)" />
        </div>
      </div>
    </div>
  );
}

function KpiStrip({ detail }: { detail: LicenseDetailRow }) {
  const expTone = expiryTone(detail.expiresAt);
  const issued = fmtDateSec(detail.issuedAt);
  const expires = fmtDateSec(detail.expiresAt);
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 }}>
      <Kpi
        label="Max instances"
        value={String(detail.maxInstances === -1 ? "∞" : detail.maxInstances)}
      />
      <Kpi label="HWID" value={detail.hwidFingerprint ? "bound" : "unbound"} />
      <Kpi label="Issued" value={issued} mono />
      <Kpi label="Expires" value={expires} mono tone={expTone} />
    </div>
  );
}

function Kpi({
  label,
  value,
  mono,
  tone,
  hint,
}: {
  label: string;
  value: string;
  mono?: boolean;
  tone?: "danger" | "warn" | "ok" | "neutral";
  hint?: string;
}) {
  const color =
    tone === "danger" ? "var(--hq-danger)" : tone === "warn" ? "var(--hq-warn)" : "var(--hq-text)";
  return (
    <div
      style={{
        padding: 14,
        background: "var(--hq-bg-input)",
        border: "1px solid var(--hq-border-input)",
        borderRadius: "var(--hq-radius-md)",
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <span
        className="hq-mono"
        style={{
          fontSize: 9.5,
          color: "var(--hq-text-dim)",
          letterSpacing: ".1em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 18,
          fontWeight: 600,
          fontFamily: mono ? "var(--hq-font-mono)" : "var(--hq-font-sans)",
          color,
        }}
      >
        {value}
      </span>
      {hint && (
        <span className="hq-mono" style={{ fontSize: 10, color: "var(--hq-text-dim)" }}>
          {hint}
        </span>
      )}
    </div>
  );
}

function TabBar({ tab, onChange }: { tab: TabId; onChange: (t: TabId) => void }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 2,
        borderBottom: "1px solid var(--hq-border)",
        marginBottom: -1,
      }}
    >
      {TABS.map((t) => {
        const Icon = Icons[t.icon];
        const active = t.id === tab;
        const danger = t.id === "danger";
        return (
          <button
            type="button"
            key={t.id}
            onClick={() => onChange(t.id)}
            style={{
              padding: "10px 14px",
              background: "transparent",
              border: "none",
              borderBottom: active
                ? `2px solid ${danger ? "var(--hq-danger)" : "var(--hq-accent)"}`
                : "2px solid transparent",
              color: active
                ? danger
                  ? "var(--hq-danger)"
                  : "var(--hq-accent)"
                : "var(--hq-text-muted)",
              cursor: "pointer",
              fontSize: 12.5,
              fontWeight: active ? 600 : 500,
              display: "inline-flex",
              gap: 6,
              alignItems: "center",
            }}
          >
            {Icon && <Icon width={13} height={13} sw={1.7} style={{ color: "currentColor" }} />}
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

interface TabBodyProps {
  tab: TabId;
  detail: LicenseDetailRow;
  onRefresh: () => void;
  toast: ReturnType<typeof useShell>["toast"];
  navigate: ReturnType<typeof useRouter>["navigate"];
}

function TabBody({ tab, detail, onRefresh, toast, navigate }: TabBodyProps) {
  switch (tab) {
    case "overview":
      return <OverviewTab detail={detail} navigate={navigate} />;
    case "telemetry":
      return <TelemetryTab licenseId={detail.licenseId} />;
    case "audit":
      return <AuditTab detail={detail} />;
    case "danger":
      return <DangerTab detail={detail} onRefresh={onRefresh} toast={toast} />;
  }
}

// ─── Overview tab ─────────────────────────────────────────────────────────

function OverviewTab({
  detail,
  navigate,
}: {
  detail: LicenseDetailRow;
  navigate: ReturnType<typeof useRouter>["navigate"];
}) {
  const features = (() => {
    try {
      const v = JSON.parse(detail.features);
      return Array.isArray(v) ? (v as string[]) : [];
    } catch {
      return [] as string[];
    }
  })();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <HqCard>
        <div
          className="hq-mono"
          style={{
            fontSize: 9.5,
            color: "var(--hq-text-dim)",
            letterSpacing: ".1em",
            textTransform: "uppercase",
            marginBottom: 10,
          }}
        >
          Metadata
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "max-content 1fr",
            gap: "6px 16px",
            fontSize: 12,
          }}
        >
          <Label>License ID</Label>
          <Value mono>{detail.licenseId}</Value>
          <Label>Customer</Label>
          <Value>
            <button
              type="button"
              onClick={() => navigate("customers", detail.customerId)}
              style={{
                background: "transparent",
                border: "none",
                padding: 0,
                color: "var(--hq-accent)",
                cursor: "pointer",
                fontFamily: "var(--hq-font-mono)",
                fontSize: 12,
              }}
            >
              {detail.customerId} →
            </button>
          </Value>
          <Label>Major version</Label>
          <Value mono>{detail.majorVersion}</Value>
          <Label>HWID</Label>
          <Value mono>{detail.hwidFingerprint ? detail.hwidFingerprint : <em>unbound</em>}</Value>
          <Label>Features</Label>
          <Value>
            {features.length === 0 ? (
              <span style={{ color: "var(--hq-text-dim)" }}>—</span>
            ) : (
              <span style={{ display: "inline-flex", gap: 6, flexWrap: "wrap" }}>
                {features.map((f) => (
                  <HqBadge key={f} tone="neutral" size="sm">
                    {f}
                  </HqBadge>
                ))}
              </span>
            )}
          </Value>
          {detail.revoked && (
            <>
              <Label>Revoked reason</Label>
              <Value>
                <span style={{ color: "var(--hq-danger)" }}>
                  {detail.revokedReason ?? "(no reason given)"}
                </span>
              </Value>
            </>
          )}
        </div>
      </HqCard>

      <HqCard>
        <div
          className="hq-mono"
          style={{
            fontSize: 9.5,
            color: "var(--hq-text-dim)",
            letterSpacing: ".1em",
            textTransform: "uppercase",
            marginBottom: 10,
          }}
        >
          Signed JSON
        </div>
        <pre
          style={{
            margin: 0,
            padding: 12,
            background: "var(--hq-bg-input)",
            border: "1px solid var(--hq-border-input)",
            borderRadius: "var(--hq-radius-md)",
            fontFamily: "var(--hq-font-mono)",
            fontSize: 10.5,
            lineHeight: 1.55,
            color: "var(--hq-text-muted)",
            overflow: "auto",
            maxHeight: 320,
          }}
        >
          {detail.signedJson}
        </pre>
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <a
            href={Api.licenseDownloadUrl(detail.licenseId)}
            download={`${detail.licenseId}.vyvern-license.json`}
            style={{ textDecoration: "none" }}
          >
            <HqButton variant="secondary" size="sm" leadingIcon={Icons.Download}>
              Download .vyvern-license.json
            </HqButton>
          </a>
          <HqButton
            variant="ghost"
            size="sm"
            leadingIcon={Icons.Copy}
            onClick={() => {
              void navigator.clipboard.writeText(detail.signedJson);
            }}
          >
            Copy to clipboard
          </HqButton>
        </div>
      </HqCard>
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="hq-mono"
      style={{
        fontSize: 10.5,
        color: "var(--hq-text-dim)",
        letterSpacing: ".08em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function Value({ children, mono }: { children: React.ReactNode; mono?: boolean }) {
  return (
    <span
      style={{
        fontFamily: mono ? "var(--hq-font-mono)" : "var(--hq-font-sans)",
        color: "var(--hq-text)",
        fontSize: 12,
        wordBreak: "break-all",
      }}
    >
      {children}
    </span>
  );
}

// ─── Telemetry tab ────────────────────────────────────────────────────────

function TelemetryTab({ licenseId }: { licenseId: string }) {
  const [data, setData] = useState<LicenseTelemetryRollup | null>(null);
  const [windowDays, setWindowDays] = useState(30);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    void (async () => {
      const res = await Api.licenseTelemetry(licenseId, windowDays);
      if (cancelled) return;
      if (!res.ok) {
        setErr(res.error.message);
        return;
      }
      setData(res.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [licenseId, windowDays]);

  if (err) return <Centered tone="danger">{err}</Centered>;
  if (!data) return <Centered tone="dim">Loading telemetry…</Centered>;

  const maxBucket = Math.max(...data.series.map((s) => s.instanceCount), 1);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <span style={{ fontSize: 11.5, color: "var(--hq-text-muted)" }}>Window</span>
        <Segmented
          value={String(windowDays)}
          onChange={(v) => setWindowDays(Number(v))}
          options={[
            { value: "7", label: "7d" },
            { value: "30", label: "30d" },
            { value: "90", label: "90d" },
          ]}
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 }}>
        <Kpi label="Live instances" value={String(data.latestInstanceCount)} />
        <Kpi
          label="Commands"
          value={data.commandInvocations.toLocaleString()}
          hint={`${data.errorTotal} errors`}
        />
        <Kpi
          label="Error rate"
          value={`${(data.errorRate * 100).toFixed(1)}%`}
          tone={data.errorRate > 0.05 ? "warn" : "ok"}
        />
        <Kpi
          label="Last seen"
          value={data.latestSeenAt ? fmtRelative(data.latestSeenAt) : "never"}
          mono
        />
      </div>

      {data.series.length > 0 && (
        <HqCard>
          <div
            className="hq-mono"
            style={{
              fontSize: 9.5,
              color: "var(--hq-text-dim)",
              letterSpacing: ".1em",
              textTransform: "uppercase",
              marginBottom: 10,
            }}
          >
            Instance count per day (max in bucket)
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              gap: 2,
              height: 80,
            }}
          >
            {data.series.map((s) => (
              <div
                key={s.bucketStart}
                title={`${new Date(s.bucketStart).toISOString().slice(0, 10)} · ${s.instanceCount} instances · ${s.commandInvocations} cmds · ${s.errorTotal} errs`}
                style={{
                  flex: 1,
                  background: s.errorTotal > 0 ? "var(--hq-warn)" : "var(--hq-accent)",
                  opacity: 0.85,
                  borderRadius: 2,
                  height: `${Math.max(4, (s.instanceCount / maxBucket) * 80)}px`,
                  minWidth: 4,
                }}
              />
            ))}
          </div>
        </HqCard>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <RankCard title="Top features" rows={data.topFeatures} tone="accent" />
        <RankCard title="Top errors" rows={data.topErrors} tone="warn" />
      </div>
    </div>
  );
}

function RankCard({
  title,
  rows,
  tone,
}: {
  title: string;
  rows: Array<{ key: string; count: number }>;
  tone: "accent" | "warn";
}) {
  return (
    <HqCard>
      <div
        className="hq-mono"
        style={{
          fontSize: 9.5,
          color: tone === "warn" ? "var(--hq-warn)" : "var(--hq-text-dim)",
          letterSpacing: ".1em",
          textTransform: "uppercase",
          marginBottom: 10,
        }}
      >
        {title}
      </div>
      {rows.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--hq-text-dim)" }}>—</div>
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {rows.map((r) => (
            <li
              key={r.key}
              style={{
                display: "flex",
                gap: 10,
                padding: "5px 0",
                borderBottom: "1px dashed var(--hq-border-input)",
                fontSize: 12,
              }}
            >
              <span className="hq-mono" style={{ flex: 1, color: "var(--hq-text)", fontSize: 11 }}>
                {r.key}
              </span>
              <span
                className="hq-mono"
                style={{
                  color: tone === "warn" ? "var(--hq-warn)" : "var(--hq-accent)",
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                {r.count}
              </span>
            </li>
          ))}
        </ul>
      )}
    </HqCard>
  );
}

function Segmented({
  options,
  value,
  onChange,
}: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div
      style={{
        display: "inline-flex",
        padding: 3,
        gap: 2,
        background: "var(--hq-bg-input)",
        borderRadius: "var(--hq-radius-lg)",
        border: "1px solid var(--hq-border-input)",
      }}
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            type="button"
            key={o.value}
            onClick={() => onChange(o.value)}
            style={{
              padding: "0 10px",
              height: 26,
              borderRadius: "var(--hq-radius-md)",
              background: active ? "var(--hq-bg-active)" : "transparent",
              color: active ? "var(--hq-text)" : "var(--hq-text-muted)",
              fontSize: 11.5,
              border: "none",
              cursor: "pointer",
              fontWeight: 500,
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Audit tab ────────────────────────────────────────────────────────────

function AuditTab({ detail }: { detail: LicenseDetailRow }) {
  // Re-uses the customer-audit endpoint w/ customerId filter — it surfaces
  // license-scoped rows too. Cheap and correct: the row's customerId is on
  // the detail object.
  const [rows, setRows] = useState<CustomerAuditRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await CustomersApi.auditTimeline(detail.customerId, 300);
      if (cancelled) return;
      if (!res.ok) {
        setErr(res.error.message);
        return;
      }
      // Filter to license-scoped rows for this licenseId.
      setRows(
        res.data.filter(
          (r) =>
            (r.targetType === "license" && r.targetId === detail.licenseId) ||
            (typeof r.meta === "object" &&
              (r.meta as Record<string, unknown>).licenseId === detail.licenseId),
        ),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [detail.customerId, detail.licenseId]);

  if (err) return <Centered tone="danger">{err}</Centered>;
  if (rows == null) return <Centered tone="dim">Loading audit…</Centered>;
  if (rows.length === 0) {
    return (
      <HqCard>
        <div
          style={{ padding: 24, color: "var(--hq-text-dim)", fontSize: 13, textAlign: "center" }}
        >
          No audit rows for this license yet.
        </div>
      </HqCard>
    );
  }
  return (
    <HqCard padding={false}>
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {rows.map((r) => (
          <li
            key={r.id}
            style={{
              padding: "10px 14px",
              borderBottom: "1px solid var(--hq-border)",
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <span className="hq-mono" style={{ fontSize: 10.5, color: "var(--hq-text-dim)" }}>
              {new Date(r.at).toISOString().slice(0, 19).replace("T", " ")}
            </span>
            <span className="hq-mono" style={{ fontSize: 11, color: "var(--hq-accent)" }}>
              {r.action}
            </span>
            <span style={{ fontSize: 11, color: "var(--hq-text-muted)" }}>by {r.actor}</span>
            <span style={{ flex: 1 }} />
            <span
              className="hq-mono"
              style={{
                fontSize: 10.5,
                color: "var(--hq-text-dim)",
                maxWidth: 360,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={JSON.stringify(r.meta)}
            >
              {Object.entries(r.meta)
                .slice(0, 4)
                .map(
                  ([k, v]) => `${k}=${typeof v === "string" ? v.slice(0, 20) : JSON.stringify(v)}`,
                )
                .join(" ")}
            </span>
          </li>
        ))}
      </ul>
    </HqCard>
  );
}

// ─── Danger Zone tab ──────────────────────────────────────────────────────

function DangerTab({
  detail,
  onRefresh,
  toast,
}: {
  detail: LicenseDetailRow;
  onRefresh: () => void;
  toast: ReturnType<typeof useShell>["toast"];
}) {
  const [extendOpen, setExtendOpen] = useState(false);
  const [tierOpen, setTierOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  async function revoke() {
    const reason = prompt("Revoke reason (lands in audit)?", "operator-initiated");
    if (!reason) return;
    setBusy("revoke");
    const res = await Api.revokeLicense(detail.licenseId, reason.trim());
    setBusy(null);
    if (!res.ok) {
      toast(res.error.message, "danger");
      return;
    }
    toast("License revoked.", "warn");
    onRefresh();
  }

  async function unrevoke() {
    setBusy("unrevoke");
    const res = await Api.unrevokeLicense(detail.licenseId);
    setBusy(null);
    if (!res.ok) {
      toast(res.error.message, "danger");
      return;
    }
    toast("License restored.", "success");
    onRefresh();
  }

  async function resend() {
    setBusy("resend");
    const res = await Api.resendSingleLicenseEmail(detail.licenseId);
    setBusy(null);
    if (!res.ok) {
      toast(res.error.message, res.error.code === "email_not_configured" ? "warn" : "danger");
      return;
    }
    toast(`Resent (sent=${res.data.sent}).`, "success");
    onRefresh();
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <HqCard>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Extend expiry</div>
        <div
          style={{ fontSize: 12, color: "var(--hq-text-dim)", lineHeight: 1.5, marginBottom: 12 }}
        >
          Picks a new <code>expiresAt</code> and re-signs the license — same id, new signature. Use
          "perpetual" to remove the expiry entirely.
        </div>
        <HqButton
          variant="secondary"
          size="sm"
          leadingIcon={Icons.Plus}
          onClick={() => setExtendOpen(true)}
          disabled={detail.revoked}
        >
          Extend / set perpetual
        </HqButton>
      </HqCard>

      <HqCard>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Change tier</div>
        <div
          style={{ fontSize: 12, color: "var(--hq-text-dim)", lineHeight: 1.5, marginBottom: 12 }}
        >
          Currently <strong>{detail.tier}</strong>. Switches to a new tier, re-signs. Use this for
          tier upgrades/downgrades without re-issuing a new license.
        </div>
        <HqButton
          variant="secondary"
          size="sm"
          leadingIcon={Icons.Key}
          onClick={() => setTierOpen(true)}
          disabled={detail.revoked}
        >
          Change tier
        </HqButton>
      </HqCard>

      <HqCard>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Resend license email</div>
        <div
          style={{ fontSize: 12, color: "var(--hq-text-dim)", lineHeight: 1.5, marginBottom: 12 }}
        >
          Once SMTP is wired up, this sends the signed JSON + install instructions to the customer's
          email on record. For now it returns 503 but the audit row lands.
        </div>
        <HqButton
          variant="secondary"
          size="sm"
          leadingIcon={Icons.Send ?? Icons.Inbox}
          onClick={() => void resend()}
          loading={busy === "resend"}
          disabled={busy === "resend"}
        >
          Resend
        </HqButton>
      </HqCard>

      <HqCard>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: "var(--hq-danger)" }}>
          {detail.revoked ? "Un-revoke license" : "Revoke license"}
        </div>
        <div
          style={{ fontSize: 12, color: "var(--hq-text-dim)", lineHeight: 1.5, marginBottom: 12 }}
        >
          {detail.revoked
            ? "Brings the license back to active. The original revoke reason stays on file."
            : "Flips revoked=true. Vyvern instances pick it up on the next revocation poll (~1 min)."}
        </div>
        {detail.revoked ? (
          <HqButton
            variant="secondary"
            size="sm"
            leadingIcon={Icons.Check}
            onClick={() => void unrevoke()}
            loading={busy === "unrevoke"}
            disabled={busy === "unrevoke"}
          >
            Un-revoke
          </HqButton>
        ) : (
          <HqButton
            variant="danger"
            size="sm"
            leadingIcon={Icons.Revoke}
            onClick={() => void revoke()}
            loading={busy === "revoke"}
            disabled={busy === "revoke"}
          >
            Revoke
          </HqButton>
        )}
      </HqCard>

      <ExtendLicenseModal
        open={extendOpen}
        detail={detail}
        onClose={() => setExtendOpen(false)}
        onExtended={() => {
          setExtendOpen(false);
          onRefresh();
        }}
      />
      <ChangeTierModal
        open={tierOpen}
        detail={detail}
        onClose={() => setTierOpen(false)}
        onChanged={() => {
          setTierOpen(false);
          onRefresh();
        }}
      />
    </div>
  );
}

function ExtendLicenseModal({
  open,
  detail,
  onClose,
  onExtended,
}: {
  open: boolean;
  detail: LicenseDetailRow;
  onClose: () => void;
  onExtended: () => void;
}) {
  const { toast } = useShell();
  const [dateStr, setDateStr] = useState("");
  const [perpetual, setPerpetual] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setBusy(false);
      setErr(null);
      // Default to "today + 365 days" for convenience.
      const d = new Date(Date.now() + 365 * 86_400_000);
      setDateStr(d.toISOString().slice(0, 10));
      setPerpetual(detail.expiresAt == null);
    }
  }, [open, detail.expiresAt]);

  async function submit() {
    let expiresAt: number | null;
    if (perpetual) {
      expiresAt = null;
    } else {
      const ms = Date.parse(`${dateStr}T23:59:59Z`);
      if (!Number.isFinite(ms)) {
        setErr("Pick a valid YYYY-MM-DD date.");
        return;
      }
      expiresAt = Math.floor(ms / 1000);
    }
    setBusy(true);
    setErr(null);
    const res = await Api.extendLicense(detail.licenseId, expiresAt);
    setBusy(false);
    if (!res.ok) {
      setErr(res.error.message);
      return;
    }
    toast("License extended + re-signed.", "success");
    onExtended();
  }

  return (
    <HqModal
      open={open}
      onClose={onClose}
      title={
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Icons.Plus width={14} height={14} sw={1.7} style={{ color: "var(--hq-accent)" }} />
          Extend license
        </span>
      }
      width={440}
      footer={
        <>
          <HqButton variant="ghost" onClick={onClose}>
            Cancel
          </HqButton>
          <HqButton variant="primary" loading={busy} onClick={() => void submit()}>
            Apply &amp; re-sign
          </HqButton>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <p
          style={{
            margin: 0,
            fontSize: 12.5,
            color: "var(--hq-text-muted)",
            lineHeight: 1.55,
          }}
        >
          Currently <strong>{detail.expiresAt ? fmtDateSec(detail.expiresAt) : "perpetual"}</strong>
          .
        </p>
        <label
          htmlFor="ext-perp"
          style={{ display: "inline-flex", gap: 8, alignItems: "center", fontSize: 12 }}
        >
          <input
            id="ext-perp"
            type="checkbox"
            checked={perpetual}
            onChange={(e) => setPerpetual(e.target.checked)}
          />
          Make perpetual (no expiry)
        </label>
        {!perpetual && (
          <HqInput
            value={dateStr}
            onChange={(e) => setDateStr(e.target.value)}
            placeholder="YYYY-MM-DD"
            mono
            hint="End-of-day UTC on that date"
          />
        )}
        {err && (
          <div
            style={{
              padding: "8px 12px",
              borderRadius: "var(--hq-radius-md)",
              background: "var(--hq-danger-soft)",
              color: "var(--hq-danger)",
              fontSize: 12,
              border: "1px solid rgba(251,113,133,.25)",
            }}
          >
            {err}
          </div>
        )}
      </div>
    </HqModal>
  );
}

function ChangeTierModal({
  open,
  detail,
  onClose,
  onChanged,
}: {
  open: boolean;
  detail: LicenseDetailRow;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { toast } = useShell();
  const [pick, setPick] = useState<Tier>(detail.tier);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setPick(detail.tier === "pro" ? "beta" : "pro");
      setBusy(false);
      setErr(null);
    }
  }, [open, detail.tier]);

  async function submit() {
    setBusy(true);
    setErr(null);
    const res = await Api.changeLicenseTier(detail.licenseId, pick);
    setBusy(false);
    if (!res.ok) {
      setErr(res.error.message);
      return;
    }
    toast(`Tier changed to ${pick}.`, "success");
    onChanged();
  }

  const TIERS: Tier[] = ["free", "pro", "beta"];
  return (
    <HqModal
      open={open}
      onClose={onClose}
      title={
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Icons.Key width={14} height={14} sw={1.7} style={{ color: "var(--hq-accent)" }} />
          Change tier
        </span>
      }
      width={440}
      footer={
        <>
          <HqButton variant="ghost" onClick={onClose}>
            Cancel
          </HqButton>
          <HqButton
            variant="primary"
            loading={busy}
            onClick={() => void submit()}
            disabled={pick === detail.tier}
          >
            Switch &amp; re-sign
          </HqButton>
        </>
      }
    >
      <p
        style={{
          margin: "0 0 10px",
          fontSize: 12.5,
          color: "var(--hq-text-muted)",
          lineHeight: 1.55,
        }}
      >
        Currently <strong>{detail.tier}</strong>. The license id stays the same; the signed JSON
        gets a new <code>issuedAt</code> + <code>tier</code> + signature.
      </p>
      <div style={{ display: "flex", gap: 8 }}>
        {TIERS.map((t) => (
          <button
            type="button"
            key={t}
            onClick={() => setPick(t)}
            style={{
              flex: 1,
              padding: "10px 12px",
              background: pick === t ? "var(--hq-bg-active)" : "var(--hq-bg-input)",
              border: `1px solid ${pick === t ? "var(--hq-accent)" : "var(--hq-border-input)"}`,
              borderRadius: "var(--hq-radius-md)",
              color: "var(--hq-text)",
              cursor: "pointer",
              fontSize: 13,
              fontWeight: pick === t ? 600 : 500,
            }}
          >
            {t}
          </button>
        ))}
      </div>
      {err && (
        <div
          style={{
            marginTop: 10,
            padding: "8px 12px",
            borderRadius: "var(--hq-radius-md)",
            background: "var(--hq-danger-soft)",
            color: "var(--hq-danger)",
            fontSize: 12,
            border: "1px solid rgba(251,113,133,.25)",
          }}
        >
          {err}
        </div>
      )}
    </HqModal>
  );
}

function fmtRelative(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 60_000) return "just now";
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)} min ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3600_000)} h ago`;
  if (diff < 30 * 86_400_000) return `${Math.floor(diff / 86_400_000)} d ago`;
  return new Date(ms).toISOString().slice(0, 10);
}
